import type { ReactNode } from 'react';

import styles from './table-skeleton.module.scss';

/** Deterministic-but-organic cell widths so the placeholder reads like real data. */
const WIDTHS = ['62%', '46%', '54%', '38%', '70%', '50%', '44%', '58%'];

export type TableSkeletonProps = {
  /** Number of columns to mirror (one shimmer cell each). */
  columns: number;
  /** Body rows to render. Default 8. */
  rows?: number;
  /** Real column titles for the header band, in place of its shimmer bars (117774 8a). Opt-in:
   *  unset keeps every existing table's loading state exactly as it was. */
  headers?: ReactNode[];
};

/**
 * Structured loading placeholder for `<DataTable>` — a header band plus shimmering rows/cells
 * that mirror the table's column grid, instead of generic full-width bars.
 */
export function TableSkeleton({ columns, rows = 8, headers }: TableSkeletonProps) {
  const cols = Math.max(columns, 1);
  const gridStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };

  return (
    <div className={styles.card} data-testid="table-skeleton" aria-busy="true" aria-live="polite">
      <div className={styles.head} style={gridStyle}>
        {Array.from({ length: cols }).map((_, c) =>
          headers ? (
            <div key={c} className={styles.headTitle}>
              {headers[c]}
            </div>
          ) : (
            <div key={c} className={styles.headCell} data-testid="table-skeleton-cell">
              <span className={styles.bar} style={{ width: '52%' }} />
            </div>
          ),
        )}
      </div>
      <div className={styles.body}>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className={styles.row} style={gridStyle} data-testid="table-skeleton-row">
            {Array.from({ length: cols }).map((_, c) => (
              <div key={c} className={styles.cell} data-testid="table-skeleton-cell">
                <span className={styles.bar} style={{ width: WIDTHS[(r + c) % WIDTHS.length] }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
